import type { TWidget } from 'widgets/Widget';
import { submitFormJson } from 'widgets/toolbox/ajax';
import type { TRefElementInstance } from 'widgets/toolbox/RefElement';
import type { TLoyaltyPointsCartModalInstance } from 'widgets/cart/LoyaltyPointsCartModal';

export type TLoyaltyPointsPayload = {
    points: number;
};

export type TLoyaltyPointsResponse = {
    success?: boolean;
    redirectUrl?: string;
    errorMessage?: string;
    cart?: Record<string, unknown>;
    order?: Record<string, unknown>;
    [key: string]: unknown;
};

/**
 * @description Base LoyaltyPointsCartPayment implementation
 * @param Widget Base widget for extending
 * @returns LoyaltyPointsCartPayment class
 */
function LoyaltyPointsCartPaymentClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory cart
     * @class LoyaltyPointsCartPayment
     * @augments Widget
     * @classdesc Represents Loyalty Points payment selector on Cart and Checkout pages.
     * Renders a dropdown that allows selecting a number of loyalty points and
     * sends a POST request with the selected amount when the selection changes.
     * On success, emits cart/order update events to refresh totals and mini-cart.
     */
    class LoyaltyPointsCartPayment extends Widget {
        prefs() {
            return {
                endpointUrl: '',
                pointsSelect: 'pointsSelect',
                message: 'message',
                classesBusy: 'm-busy',
                msgApplied: '',
                msgRemoved: '',
                msgError: '',
                msgButtonApplied: '',
                accessibilityAlerts: <TAccessibilityAlerts>{},
                ...super.prefs()
            };
        }

        init() {
            super.init();

            const selectEl = this.ref(this.prefs().pointsSelect).get();

            if (!(selectEl instanceof HTMLSelectElement) || !selectEl.value) {
                return;
            }

            const points = Number(selectEl.value);

            if (isNaN(points)) {
                return;
            }

            const msg = points > 0
                ? this.prefs().msgApplied.replace('{0}', String(points))
                : this.prefs().msgRemoved;

            this.showMessage(msg, false);
        }

        /**
         * @description Handles change event on loyalty points dropdown.
         */
        handlePointsChange(_selectRef: TRefElementInstance, event: Event) {
            const selectEl = <HTMLSelectElement>event.target;
            const rawValue = selectEl && selectEl.value;

            if (rawValue === '' || rawValue === undefined || rawValue === null) {
                this.clearMessage();

                return;
            }

            const points = Number(rawValue);

            if (isNaN(points)) {
                return;
            }

            const url = this.prefs().endpointUrl;

            if (!url) {
                return;
            }

            const payload: TLoyaltyPointsPayload = { points };

            this.ref('self').addClass(this.prefs().classesBusy);

            submitFormJson(url, payload, 'POST')
                .then((response: TLoyaltyPointsResponse) => {
                    this.onApplied(response, points);
                })
                .catch(() => {
                    this.showMessage(this.prefs().msgError, true);
                })
                .finally(() => {
                    this.ref('self').removeClass(this.prefs().classesBusy);
                });
        }

        /**
         * @description Handles server response after applying loyalty points.
         */
        onApplied(response: TLoyaltyPointsResponse, points: number) {
            if (response && response.redirectUrl) {
                window.location.assign(response.redirectUrl);

                return;
            }

            if (response && response.success === false) {
                this.showMessage(response.errorMessage || this.prefs().msgError, true);

                return;
            }

            if (response && response.cart) {
                // minicart.updated triggers CartMgr.renderCart() which re-renders
                // the full cart page including order totals summary via diff-dom
                this.eventBus().emit('minicart.updated', response.cart);
            }

            if (response && response.order) {
                this.eventBus().emit('order.updated', response.order);
            }

            const msg = points > 0 ? this.prefs().msgApplied.replace('{0}', String(points)) : this.prefs().msgRemoved;

            this.showMessage(msg, false);

            const accessibilityAlert = msg;

            setTimeout(() => this.eventBus().emit('alert.show', { accessibilityAlert }), 400);
        }

        showMessage(message: string, isError = false) {
            const messageRef = this.ref(this.prefs().message);

            if (!messageRef.exists()) {
                return;
            }

            messageRef.setText(message).toggleClass('m-error', isError).toggleClass('m-success', !isError).show();
        }

        clearMessage() {
            const messageRef = this.ref(this.prefs().message);

            if (!messageRef.exists()) {
                return;
            }

            messageRef.setText('').hide();
        }

        openModal() {
            this.getById('loyaltyPointsCartModal', (modal: TLoyaltyPointsCartModalInstance) => {
                modal.showModal();
            });
        }

        onModalSaved(_modal: TLoyaltyPointsCartModalInstance, { amount }: { amount: string }) {
            const template = this.prefs().msgButtonApplied;

            if (!template) {
                return;
            }

            this.ref('checkoutButton').setText(template.replace('{0}', amount));
        }
    }

    return LoyaltyPointsCartPayment;
}

export type TLoyaltyPointsCartPayment = ReturnType<typeof LoyaltyPointsCartPaymentClassCreator>;

export type TLoyaltyPointsCartPaymentInstance = InstanceType<TLoyaltyPointsCartPayment>;

export default LoyaltyPointsCartPaymentClassCreator;
